/* ──────────────────────────────────────────────────────────────
 *  useExpectedTestValues — fetch expected physical test values
 *  from the backend.
 *
 *  The backend is the single source of truth for the FEA→test
 *  inverse solver (Levenberg-Marquardt). The frontend only
 *  consumes and displays the returned values.
 *
 *  Only applies to A1, B1, C1 types. D1 / BYD types use the
 *  design value directly (no FEA→test conversion).
 * ────────────────────────────────────────────────────────────── */

import { useEffect, useState, useRef } from 'react';
import { useInputStore } from '@/stores/useInputStore';
import { useBushingStore } from '@/stores/useBushingStore';
import type { BushingType } from '@/types/bushing';
import type { SampleRow } from '@/types/sample';
import {
  computeExpectedTestValues,
  type ExpectedTestValuesResponse,
} from '@/api/expectedTestValuesApi';

export interface ExpectedTestValues {
  Cx: number | null;
  Cz: number | null;
  Torsion: number | null;
  Conical: number | null;
}

const EMPTY: ExpectedTestValues = {
  Cx: null,
  Cz: null,
  Torsion: null,
  Conical: null,
};

/** Whether this bushing type supports FEA→test conversion. */
function supportsConversion(bushingType: BushingType): boolean {
  return bushingType === 'A1' || bushingType === 'B1' || bushingType === 'C1';
}

export interface ExpectedTestValuesResult {
  /** Expected physical test values (null per-field if not computable). */
  expected: ExpectedTestValues;
  /** Whether expected test values apply (false for D1/BYD types). */
  applicable: boolean;
  /** Whether a backend request is currently in-flight. */
  loading: boolean;
}

/**
 * Fetch expected physical test values from the backend for the given sample.
 *
 * Automatically re-fetches whenever the selected sample's stiffness values,
 * shore hardness, compound family, or amplitude inputs change.
 */
export function useExpectedTestValues(
  selectedRow: SampleRow | null,
): ExpectedTestValuesResult {
  const bushingType = useBushingStore((s) => s.bushingType);
  const compoundFamily = useInputStore((s) => s.compoundFamily);
  const shoreA = useInputStore((s) => s.shoreA);
  const amplitudes = useInputStore((s) => s.target.amplitudes);
  const sleeveThickness = useInputStore((s) => s.sleeveThickness);

  const applicable = supportsConversion(bushingType);

  const [expected, setExpected] = useState<ExpectedTestValues>(EMPTY);
  const [loading, setLoading] = useState(false);

  // Use a ref to track the latest request and discard stale responses
  const requestIdRef = useRef(0);

  // Keep a ref to the full selectedRow for sending in the API call,
  // while only using stable primitive values as effect dependencies.
  const selectedRowRef = useRef(selectedRow);
  selectedRowRef.current = selectedRow;

  // Extract the FEA values as primitives so the effect only fires
  // when the actual numeric values change (not on object reference changes).
  const Cx_FEA = selectedRow?.['Cx'];
  const Cz_FEA = selectedRow?.['Cz'];
  const Tor_FEA = selectedRow?.['Torsion'];

  // Stable amplitude primitives
  const CxA = Number(amplitudes['Cx']) || 0;
  const CzA = Number(amplitudes['Cz']) || 0;
  const TorA = Number(amplitudes['Torsion']) || 0;

  useEffect(() => {
    // Reset when not applicable or no sample selected
    if (!applicable || !selectedRowRef.current) {
      setExpected(EMPTY);
      setLoading(false);
      return;
    }

    // Validate required fields before calling backend
    if (CxA <= 0 || CzA <= 0 || TorA <= 0) {
      setExpected(EMPTY);
      setLoading(false);
      return;
    }

    // Validate FEA values exist in the sample
    if (
      typeof Cx_FEA !== 'number' ||
      typeof Cz_FEA !== 'number' ||
      typeof Tor_FEA !== 'number' ||
      !isFinite(Cx_FEA) ||
      !isFinite(Cz_FEA) ||
      !isFinite(Tor_FEA)
    ) {
      setExpected(EMPTY);
      setLoading(false);
      return;
    }

    // AbortController — cancels the previous request on re-fire (React StrictMode
    // double-invokes effects; this ensures only ONE request reaches the backend).
    const abortController = new AbortController();

    // Increment request ID and capture for staleness check
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    // Debounce: wait briefly so that rapid successive dependency changes
    // (e.g. shoreA change + subsequent row FEA value update from shore scaling)
    // are batched into a single API call.
    const debounceTimer = setTimeout(() => {
      if (abortController.signal.aborted) return;

      computeExpectedTestValues(
        {
          bushing_type: bushingType,
          material: compoundFamily,
          shore_a: shoreA,
          sleeve_thickness: sleeveThickness,
          CxA,
          CzA,
          TorA,
          sample: selectedRowRef.current as Record<string, unknown>,
        },
        { signal: abortController.signal },
      )
        .then((response: ExpectedTestValuesResponse) => {
          // Only update state if this is still the latest request
          if (currentRequestId === requestIdRef.current) {
            setExpected({
              Cx: response.Cx,
              Cz: response.Cz,
              Torsion: response.Torsion,
              Conical: response.Conical,
            });
            setLoading(false);
          }
        })
        .catch((err) => {
          // Ignore aborted requests (effect cleanup or superseded calls)
          if (err?.name === 'CanceledError' || abortController.signal.aborted) return;
          // On error, clear values (fail silently in UI)
          if (currentRequestId === requestIdRef.current) {
            setExpected(EMPTY);
            setLoading(false);
          }
        });
    }, 100);

    // Cleanup: cancel debounce timer and abort in-flight request
    // when effect re-fires or component unmounts
    return () => {
      clearTimeout(debounceTimer);
      abortController.abort();
    };
  }, [
    applicable,
    Cx_FEA,
    Cz_FEA,
    Tor_FEA,
    CxA,
    CzA,
    TorA,
    compoundFamily,
    shoreA,
    bushingType,
    sleeveThickness,
  ]);

  return { expected, applicable, loading };
}
